// ---------------------------------------------------------------------------
// Lead Form Renderer
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";
import {
  buildFormAction,
  buildHiddenFields,
  buildTurnstileWidget,
  renderField,
  buildConditionalLogicScript,
  formRequiresMultipart,
  type FormField,
  type CrmFollowUpPolicy,
} from "../form-utils.js";

interface LeadFormProps {
  heading?: string;
  fields: FormField[];
  submitText: string;
  successMessage?: string;
  consentText?: string;
  honeypot?: boolean;
  crmFollowUpPolicy?: CrmFollowUpPolicy;
}

function render(props: Record<string, unknown>, ctx: RenderContext): string {
  const p = props as unknown as LeadFormProps;

  const fields = Array.isArray(p.fields) ? p.fields : [];
  const submitText = p.submitText ?? "Submit";
  const successMessage = p.successMessage ?? "Thank you! We'll be in touch.";

  const headingHtml = p.heading
    ? `<h2 class="ease-form-heading">${text(p.heading)}</h2>\n  `
    : "";

  const fieldsHtml = fields.map((f) => renderField(f)).join("\n  ");

  const consentHtml = p.consentText
    ? `\n  <div class="ease-field ease-field-consent">
    <label><input type="checkbox" name="_consent" required aria-required="true"> ${text(p.consentText)}</label>
  </div>`
    : "";

  const hasConditions = fields.some((f) => f.conditions?.length);
  const conditionalScript = hasConditions ? `\n${buildConditionalLogicScript()}` : "";
  const encodingAttr = formRequiresMultipart(fields) ? ' enctype="multipart/form-data"' : "";
  const turnstileWidget = buildTurnstileWidget(ctx);
  const turnstileHtml = turnstileWidget ? `\n    ${turnstileWidget}` : "";

  return `<section class="ease-section ease-lead-form" aria-label="${attr(p.heading ?? "Contact form")}">
  ${headingHtml}<form method="POST" action="${attr(buildFormAction(ctx))}"${encodingAttr} novalidate data-ease-form="lead" data-success-message="${attr(successMessage)}">
    ${buildHiddenFields(ctx, { crmFollowUpPolicy: p.crmFollowUpPolicy })}
    ${fieldsHtml}${consentHtml}${turnstileHtml}
    <div class="ease-field ease-field-submit">
      <button type="submit" class="ease-btn ease-btn-primary">${text(submitText)}</button>
    </div>
  </form>${conditionalScript}
</section>`;
}

registerRenderer("lead-form", { render });
